import { useState } from "react";
import TableSeatingIcon from "./TableSeatingIcon";

interface Props {
  open: boolean;
  onClose: () => void;
  tableNo: string;
  timer?: string;
  occupiedCount?: number;
  onStopTime?: (data: { guests: string; mobile: string; note: string }) => void;
}

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#F15A29" strokeWidth="2" />
    <path d="M12 7v5l3 2" stroke="#F15A29" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M6 9l6 6 6-6" stroke="#8F9098" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GUEST_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8"];
const StopTimerPopup = ({ open, onClose, tableNo, timer = "00:32:10", occupiedCount = 3, onStopTime }: Props) => {
  const [guests, setGuests] = useState("");
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [mobile, setMobile] = useState("9850894726");
  const [note, setNote] = useState("");

  if (!open) return null;

  const handleStop = () => {
    onStopTime?.({ guests, mobile, note });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center" onClick={onClose}  >
      <div className="absolute inset-0 backdrop-blur-sm" style={{ backgroundColor: "rgba(0,0,0,0.7)" }} />
      <div className="relative w-full max-w-[430px] bg-white rounded-t-[20px] overflow-hidden flex flex-col max-h-[90vh] stop-timer-sheet" onClick={(e) => e.stopPropagation()} >
        <style>
          {`@keyframes stopTimerSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
            .stop-timer-sheet { animation: stopTimerSlideUp 240ms ease-out; }`}
        </style>
        <div className="flex justify-center pt-2 pb-1">
          <span className="block w-10 h-1 rounded-full bg-[#E8E9F1]" />
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center bg-transparent border-0 p-0"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-4 pt-6 pb-4 flex flex-col gap-4">
          {/* Table card - list style */}
          <div className="w-full bg-white rounded-[6px] border border-[#E6EEFF] flex items-center gap-3 p-3 py-2">
            <TableSeatingIcon number={tableNo} className="w-12 h-12 shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <span className="font-sansation font-bold text-[#111] text-[16px]">
                {tableNo}
              </span>
              <div className="flex items-center gap-1">
                <ClockIcon />
                <span className="font-sansation font-bold text-[#F15A29] text-[12px] tracking-[1.5px]">
                  {timer}
                </span>
              </div>
            </div>
            <span className="px-2 py-0.5 rounded-full font-opensans font-semibold text-white text-[10px] shrink-0" style={{ backgroundColor: "#F15A29" }} >
              {occupiedCount} Occupied
            </span>
          </div>

          {/* Number Of Guests */}
          <div className="flex flex-col gap-2">
            <label className="font-opensans text-[14px] text-[#111]">
              Number Of Guests
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setGuestsOpen((o) => !o)}
                className="w-full h-[47px] rounded-[6px] border bg-white px-3 font-opensans text-[14px] text-left flex items-center justify-between"
                style={{ borderColor: "#C5C6CC", color: guests ? "#111" : "#8F9098" }}
              >
                <span>{guests || "PAX (Number of Guest)"}</span>
                <ChevronDown />
              </button>
              {guestsOpen && (
                <ul className="absolute z-10 top-[52px] left-0 right-0 bg-white border rounded-[10px] shadow-[0_4px_20px_rgba(0,0,0,0.12)] overflow-hidden max-h-[200px] overflow-y-auto" style={{ borderColor: "#C5C6CC" }} >
                  {GUEST_OPTIONS.map((g) => (
                    <li key={g}>
                      <button
                        type="button"
                        onClick={() => {
                          setGuests(g);
                          setGuestsOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 font-opensans text-[14px] text-[#111] hover:bg-[#F4F4F5] bg-white border-0"
                      >
                        {g}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Mobile Number */}
          <div className="flex flex-col gap-2">
            <label className="font-opensans text-[14px] text-[#111]">
              Mobile Number
            </label>
            <input
              type="tel"
              inputMode="numeric"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="Enter mobile number"
              className="w-full h-[47px] rounded-[6px] border bg-white px-3 font-opensans text-[14px] text-[#111] outline-none placeholder:text-[#8F9098]"
              style={{ borderColor: "#C5C6CC" }}
            />
          </div>

          {/* Avg Order Value + Visit info */}
          <div className="flex justify-between gap-3">
            <div className="flex flex-col">
              <span className="font-opensans text-[14px] text-[#111]">
                Avg Order Value
              </span>
              <span className="font-opensans font-bold text-[#67AF7A] text-[14px] mt-1">
                Rs. 25,000
              </span>
            </div>
            <div className="flex flex-col text-right">
              <p className="font-opensans text-[14px] text-[#111]">
                <span className="font-bold text-[#F25138]">14</span> Visits in last 30 days
              </p>
              <p className="font-opensans text-[14px] text-[#111] mt-1">
                Last Visit <span className="font-bold text-[#F25138]">21st Aug 2025</span>
              </p>
            </div>
          </div>

          {/* Customer Note */}
          <div className="flex flex-col gap-2">
            <label className="font-opensans text-[14px] text-[#111]">
              Customer Note
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter Note"
              rows={3}
              className="w-full rounded-[6px] border bg-white px-3 py-3 font-opensans text-[14px] text-[#111] outline-none placeholder:text-[#8F9098] resize-none"
              style={{ borderColor: "#C5C6CC" }}
            />
          </div>

          {/* Stop Time button */}
          <button type="button" onClick={handleStop} className="w-full h-11 rounded-[6px] bg-transparent font-sansation font-bold text-[14px] tracking-[-0.28px] uppercase border-2 flex items-center justify-center gap-2" style={{ borderColor: "#F15A29", color: "#F15A29" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="#F15A29" strokeWidth="2" />
              <path d="M12 7v5l3 2" stroke="#F15A29" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Stop Time
          </button>
        </div>
      </div>
    </div>
  );
};

export default StopTimerPopup;
